import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ttApi, ttAuth } from "../../lib/ttApi";
import {
  Loader2, Shield, LogIn, Download, RefreshCcw, ArrowLeft, FileText,
  Beaker, Bug, GitBranch, Rocket, Database, GitCompareArrows,
  AlertTriangle, HeartPulse, Sparkles, CheckCircle2, XCircle, Clock,
} from "lucide-react";

// ─── Testing Admin login ─────────────────────────────────────────────────
export function TestingAdminLogin() {
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await ttApi.adminLogin(f.email.trim(), f.password);
      ttAuth.set(r.token);
      nav("/test-tech/home", { replace: true });
    } catch (e) { setErr(e.message || "Login failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 px-4">
      <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-md shadow-sm p-6 w-full max-w-sm" data-testid="testing-admin-form">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500 inline-flex items-center gap-2"><Shield size={11}/> Capittance · Testing Admin</div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-zinc-900 mt-1">Admin sign-in</h1>
        <p className="text-xs text-zinc-500 mt-1">Use your Capittance admin credentials to see every program at once.</p>
        <div className="mt-5 space-y-3">
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Admin email</div>
            <input type="email" value={f.email} onChange={e => setF({...f, email: e.target.value})}
                   className="fld py-2 px-3 w-full" required data-testid="testing-admin-email"/>
          </label>
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Password</div>
            <input type="password" value={f.password} onChange={e => setF({...f, password: e.target.value})}
                   className="fld py-2 px-3 w-full" required data-testid="testing-admin-password"/>
          </label>
          {err && <div className="p-2 bg-red-50 border-l-4 border-red-500 text-sm text-red-800" data-testid="testing-admin-error">{err}</div>}
          <button type="submit" disabled={busy} className="btn btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="testing-admin-submit">
            {busy ? <><Loader2 className="animate-spin" size={14}/> Signing in…</> : <><LogIn size={14}/> Sign in</>}
          </button>
          <Link to="/test-tech" className="block text-center text-xs text-zinc-500 hover:text-zinc-900">
            Regular test-tech sign in →
          </Link>
        </div>
      </form>
    </div>
  );
}

// ─── Unified View ────────────────────────────────────────────────────────
function Section({ icon: Icon, title, children, accent = "#4A0F1A", testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-3" style={{ background: "linear-gradient(90deg, " + accent + "08 0%, transparent 100%)" }}>
        <div className="w-8 h-8 rounded-sm grid place-items-center text-white shrink-0" style={{ background: accent }}>
          <Icon size={14}/>
        </div>
        <div className="font-display text-base font-extrabold tracking-tight">{title}</div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function KpiRow({ items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {items.map((it, i) => (
        <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{it.label}</div>
          <div className={`font-display text-xl font-extrabold mt-1 ${it.tone === "red" ? "text-red-700" : it.tone === "amber" ? "text-amber-700" : it.tone === "green" ? "text-emerald-700" : "text-zinc-900"}`}>{it.value}</div>
          {it.sub && <div className="text-[10px] text-zinc-500 mt-0.5">{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

export function UnifiedView() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [u, setU] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [exporting, setExporting] = useState(null);
  const timerRef = useRef(null);

  const refresh = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await ttApi.unified(pid);
      setU(r);
      setLastRefresh(new Date());
      setErr("");
    } catch (e) {
      const m = e.message || "";
      setErr(m === "program_not_in_scope" ? "You don't have access to this program." : m || "Failed to load");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [pid]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRefresh) {
      timerRef.current = setInterval(() => { if (document.visibilityState === "visible") refresh(true); }, 15000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line
  }, [autoRefresh, pid]);

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      const url = kind === "pdf" ? ttApi.exportPdfUrl(pid) : ttApi.exportPptxUrl(pid);
      const token = ttAuth.get();
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      const cd = resp.headers.get("Content-Disposition") || "";
      a.download = (cd.match(/filename="([^"]+)"/) || [])[1] || `capittance-${pid}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(dlUrl), 2000);
    } catch (e) {
      alert(e.message || "Export failed");
    } finally {
      setExporting(null);
    }
  };

  if (loading && !u) {
    return <div className="min-h-screen grid place-items-center text-zinc-500"><Loader2 className="animate-spin"/></div>;
  }
  if (err && !u) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 px-4">
        <div className="bg-white border border-red-200 rounded-md p-6 max-w-md text-center">
          <div className="text-red-700 font-semibold">{err}</div>
          <Link to="/test-tech/home" className="btn btn-ghost btn-sm mt-4 inline-block" data-testid="tt-unified-back-tiles">Back to tiles</Link>
        </div>
      </div>
    );
  }
  if (!u) return null;

  const riskColor = { LOW: "text-emerald-700", MEDIUM: "text-amber-700", HIGH: "text-orange-700", CRITICAL: "text-red-700" }[u.risk.band] || "text-zinc-900";
  const decColor = u.golive.decision === "GO" ? "green" : u.golive.decision === "Conditional GO" ? "amber" : u.golive.decision === "NO-GO" ? "red" : "default";

  return (
    <main className="max-w-7xl mx-auto px-6 py-6" data-testid="tt-unified">
      {/* Sticky action bar */}
      <div className="sticky top-0 z-20 bg-zinc-50/95 backdrop-blur -mx-6 px-6 py-3 border-b border-zinc-200 mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/test-tech/home" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 shrink-0"><ArrowLeft size={13}/> Tiles</Link>
          <div className="min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Unified Testing View · {u.program.code || "—"}</div>
            <div className="font-display text-lg font-extrabold tracking-tight truncate">{u.program.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1 text-[11px] text-zinc-600 font-mono cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} data-testid="tt-unified-autorefresh"/>
            Auto-refresh 15s
          </label>
          <button onClick={() => refresh(false)} className="btn btn-ghost btn-sm inline-flex items-center gap-1 text-xs" data-testid="tt-unified-refresh">
            {loading ? <Loader2 className="animate-spin" size={12}/> : <RefreshCcw size={12}/>}
            Refresh
          </button>
          <span className="text-[10px] font-mono text-zinc-500 hidden md:inline">
            {lastRefresh ? `updated ${lastRefresh.toLocaleTimeString()}` : ""}
          </span>
          <button onClick={() => doExport("pdf")} disabled={exporting === "pdf"} className="btn btn-secondary btn-sm inline-flex items-center gap-1 text-xs" data-testid="tt-unified-pdf">
            {exporting === "pdf" ? <Loader2 className="animate-spin" size={12}/> : <Download size={12}/>}
            PDF
          </button>
          <button onClick={() => doExport("pptx")} disabled={exporting === "pptx"} className="btn btn-primary btn-sm inline-flex items-center gap-1 text-xs" data-testid="tt-unified-pptx">
            {exporting === "pptx" ? <Loader2 className="animate-spin" size={12}/> : <Download size={12}/>}
            PowerPoint
          </button>
        </div>
      </div>

      {/* Hero: Risk + Go-Live decision */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div className="bg-white border border-zinc-200 rounded-md p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-2"><Sparkles size={11}/> Deterministic Go-Live Risk</div>
          <div className="flex items-baseline gap-4 mt-1">
            <div className={`font-display text-5xl font-extrabold tracking-tight ${riskColor}`}>{u.risk.score}</div>
            <div className={`font-mono text-sm uppercase tracking-widest ${riskColor}`}>/ 100 · {u.risk.band}</div>
          </div>
          <ul className="mt-3 space-y-0.5 text-[11px] text-zinc-600">
            {u.risk.reasons.slice(0, 5).map((r, i) => <li key={i}>• {r}</li>)}
            {u.risk.reasons.length === 0 && <li className="text-emerald-700">• No material risk factors detected</li>}
          </ul>
        </div>
        <div className={`bg-white border rounded-md p-5 border-l-4 ${decColor === "green" ? "border-l-emerald-500" : decColor === "amber" ? "border-l-amber-500" : decColor === "red" ? "border-l-red-500" : "border-l-zinc-400"}`}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-2"><AlertTriangle size={11}/> Go-Live Decision</div>
          <div className={`font-display text-4xl font-extrabold tracking-tight mt-1 ${decColor === "green" ? "text-emerald-700" : decColor === "amber" ? "text-amber-700" : decColor === "red" ? "text-red-700" : "text-zinc-800"}`}>{u.golive.decision}</div>
          <div className="text-[11px] text-zinc-600 mt-2 font-mono">
            GO {u.golive.by_status.GO} · Conditional {u.golive.by_status["Conditional GO"]} · NO-GO {u.golive.by_status["NO-GO"]} · Pending {u.golive.by_status.Pending}
          </div>
          <Link to={`/test-tech/golive/${pid}`} className="text-xs text-zinc-700 hover:text-zinc-900 underline underline-offset-2 mt-3 inline-block" data-testid="tt-unified-golive-link">
            Edit scorecard →
          </Link>
        </div>
      </div>

      {/* Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section icon={FileText} title="Requirements · RTM" testid="unified-sec-reqs">
          <KpiRow items={[
            { label: "Total", value: u.requirements.total },
            { label: "Coverage", value: `${u.requirements.coverage_pct}%`, tone: u.requirements.coverage_pct >= 95 ? "green" : u.requirements.coverage_pct >= 80 ? "amber" : "red" },
            { label: "Uncovered", value: u.requirements.uncovered, tone: u.requirements.uncovered > 0 ? "red" : "green" },
          ]}/>
        </Section>

        <Section icon={Beaker} title="Testing" accent="#1E3A8A" testid="unified-sec-testing">
          <KpiRow items={[
            { label: "Cases", value: u.testing.cases_total, sub: `${u.testing.scenarios} scenarios` },
            { label: "Executed", value: `${u.testing.exec_pct}%`, sub: `${u.testing.cases_executed}/${u.testing.cases_total}` },
            { label: "Pass", value: `${u.testing.pass_pct}%`, tone: u.testing.pass_pct >= 90 ? "green" : u.testing.pass_pct >= 70 ? "amber" : "red" },
            { label: "Fail/Blocked", value: `${u.testing.cases_fail + u.testing.cases_blocked}`, tone: (u.testing.cases_fail + u.testing.cases_blocked) > 0 ? "red" : "green" },
          ]}/>
        </Section>

        <Section icon={Bug} title="Defects" accent="#B91C1C" testid="unified-sec-defects">
          <KpiRow items={[
            { label: "Open", value: u.defects.open, tone: u.defects.open > 0 ? "red" : "green", sub: `${u.defects.total} total` },
            { label: "Critical", value: u.defects.by_severity.Critical, tone: u.defects.by_severity.Critical > 0 ? "red" : "green" },
            { label: "High", value: u.defects.by_severity.High, tone: u.defects.by_severity.High > 0 ? "amber" : "green" },
            { label: "Medium", value: u.defects.by_severity.Medium },
          ]}/>
        </Section>

        <Section icon={GitBranch} title="Environment Readiness" accent="#065F46" testid="unified-sec-readiness">
          <KpiRow items={[
            { label: "Environments", value: u.readiness.envs },
            { label: "Avg readiness", value: `${u.readiness.avg_pct}%`, tone: u.readiness.avg_pct >= 90 ? "green" : u.readiness.avg_pct >= 70 ? "amber" : "red" },
          ]}/>
          {u.readiness.items.length > 0 && (
            <div className="mt-3 space-y-1 text-xs">
              {u.readiness.items.slice(0, 4).map(e => (
                <div key={e._id} className="flex items-center justify-between">
                  <span className="truncate pr-2"><b>{e.name}</b> <span className="text-zinc-500 font-mono text-[10px]">{e.stage}</span></span>
                  <span className={`font-mono text-[11px] ${e.rag === "green" ? "text-emerald-700" : e.rag === "amber" ? "text-amber-700" : "text-red-700"}`}>{e.readiness_pct}%</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={Rocket} title="Cutover" accent="#4A0F1A" testid="unified-sec-cutover">
          <KpiRow items={[
            { label: "Tasks", value: u.cutover.total },
            { label: "Complete", value: `${u.cutover.pct_complete}%`, tone: u.cutover.pct_complete >= 80 ? "green" : u.cutover.pct_complete >= 50 ? "amber" : "red" },
            { label: "Blocked", value: u.cutover.blocked, tone: u.cutover.blocked > 0 ? "red" : "green" },
          ]}/>
        </Section>

        <Section icon={Database} title="Test Data (TDC)" accent="#166534" testid="unified-sec-tdc">
          <KpiRow items={[
            { label: "Datasets", value: u.test_data.total },
            { label: "Fresh", value: u.test_data.fresh, tone: "green" },
            { label: "Stale", value: u.test_data.stale, tone: u.test_data.stale > 0 ? "amber" : "green" },
            { label: "Expired", value: u.test_data.expired, tone: u.test_data.expired > 0 ? "red" : "green" },
          ]}/>
        </Section>

        <Section icon={GitCompareArrows} title="System Integration (SIT)" accent="#1E40AF" testid="unified-sec-sit">
          <KpiRow items={[
            { label: "Flows", value: u.sit.total },
            { label: "Overall recon", value: `${u.sit.overall_recon_pct}%`, tone: u.sit.overall_recon_pct >= 99 ? "green" : u.sit.overall_recon_pct >= 90 ? "amber" : "red", sub: `${u.sit.total_matched.toLocaleString()}/${u.sit.total_sent.toLocaleString()}` },
            { label: "Failing", value: u.sit.fail_or_partial, tone: u.sit.fail_or_partial > 0 ? "red" : "green" },
          ]}/>
        </Section>

        <Section icon={HeartPulse} title="Hypercare" accent="#9A3412" testid="unified-sec-hypercare">
          <KpiRow items={[
            { label: "All incidents", value: u.hypercare.total },
            { label: "P1 open", value: u.hypercare.p1_open, tone: u.hypercare.p1_open > 0 ? "red" : "green" },
            { label: "P2 open", value: u.hypercare.p2_open, tone: u.hypercare.p2_open > 0 ? "amber" : "green" },
            { label: "SLA breached", value: u.hypercare.sla_breached, tone: u.hypercare.sla_breached > 0 ? "red" : "green" },
          ]}/>
        </Section>
      </div>

      {/* Go-Live sign-off table */}
      {u.golive.items.length > 0 && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="unified-golive-items">
          <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200">
            <div className="font-display text-base font-extrabold tracking-tight">Go-Live Sign-offs</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white text-xs font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
              <tr>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-left px-4 py-2">Item</th>
                <th className="text-left px-4 py-2">Owner</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {u.golive.items.map(g => (
                <tr key={g._id} className="border-t border-zinc-100 align-top">
                  <td className="px-4 py-2 text-xs text-zinc-600">{g.category}</td>
                  <td className="px-4 py-2">{g.item}</td>
                  <td className="px-4 py-2 text-xs"><div>{g.owner_name || "—"}</div><div className="text-[10px] text-zinc-500">{g.owner_role || ""}</div></td>
                  <td className="px-4 py-2 text-xs">
                    {g.signoff_status === "GO" && <span className="pill pill-green text-[10px] inline-flex items-center gap-1"><CheckCircle2 size={10}/> GO</span>}
                    {g.signoff_status === "Conditional GO" && <span className="pill bg-amber-50 text-amber-800 border border-amber-200 text-[10px] inline-flex items-center gap-1"><AlertTriangle size={10}/> Conditional</span>}
                    {g.signoff_status === "NO-GO" && <span className="pill bg-red-50 text-red-700 border border-red-200 text-[10px] inline-flex items-center gap-1"><XCircle size={10}/> NO-GO</span>}
                    {g.signoff_status === "Pending" && <span className="pill pill-neutral text-[10px] inline-flex items-center gap-1"><Clock size={10}/> Pending</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
